import { ChecklistTarget } from '@components/checklist';
import { ConditionalError } from '@components/conditional-error';
import { FieldCheckBox } from '@components/field-builder/field-checkbox';
import { FieldText } from '@components/field-builder/field-text';
import {
    Alignment,
    HelperTextContainer
} from '@components/field-builder/styles';
import { RequiredWrapper } from '@components/required';
import {
    BorderedGrid,
    BorderedGridItem,
    ReadonlyFieldLabel,
    ReadonlyFieldValue,
    SpacedPrefilledFormField
} from '@containers/form-i9/styles';
import { FormControl } from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { FieldType } from '@models/field-builder/field-type';
import { Section2ValidationErrors } from '@models/onboarding-review/section2-validation-errors';
import { createTranslationService } from '@services/factories';
import { ISection2CertificationStore } from '@stores/onboarding-review/section2-certification-store';
import { cacheTranslations } from '@utils/cache-translations';
import { DateFormatTemplate } from '@utils/date-format';
import { isTodaysLocalFormattedDate } from '@utils/date-query';
import { observer } from 'mobx-react';
import * as React from 'react';
import styled from 'styled-components';

const TodaysDatePicker = styled(DatePicker)`
    input {
      padding: 10.5px 14px;
    }
`;

const DatePickerAlignment = styled(Alignment)`
  flex: 1 1 auto;
  padding-left: 15px;
  margin-top: 5px;
`;

const DatePickerChecklistTarget = styled(ChecklistTarget)<{ hasError?: boolean; }>`
  display: flex;
  flex-direction: column;
  justify-content: ${({ hasError }) => hasError ? 'space-between' : 'flex-start'};
  height: 100%;
`

interface IProps {
  store: ISection2CertificationStore;
  validationErrors: Record<Section2ValidationErrors, boolean>;
  workerStatus: string | null;
}

enum Translation {
  signatureLabel = 'signatureLabel',
  todaysDateLabel = 'todaysDateLabel',
  titleLabel = 'titleLabel',
  lastNameLabel = 'lastNameLabel',
  firstNameLabel = 'firstNameLabel',
  businessNameLabel = 'businessNameLabel',
  businessStreetAddressLabel = 'businessStreetAddressLabel',
  cityLabel = 'cityLabel',
  stateLabel = 'stateLabel',
  zipCodeLabel = 'zipCodeLabel',
  agreeToSignLabel = 'agreeToSignLabel',
  authorisedRepresentativeTitle = 'authorisedRepresentativeTitle'
}

const Certification: React.FunctionComponent<IProps> = observer(
  ({store, validationErrors, workerStatus}) => {
    const translations = cacheTranslations(
      createTranslationService(),
      Translation,
      'section2Certification'
    );

    const validationErrorTranslations = cacheTranslations(
      createTranslationService(),
      Section2ValidationErrors,
      'completeI9ValidationErrors'
    );

      const today = new Date();
      const RECEIPT_RPLACEMENT = 'RECEIPT_REPLACEMENT';

      const hasSignedOnError = !isTodaysLocalFormattedDate(store.signedOn) &&
      validationErrors.provideSignDate;
      
      return (
      <BorderedGrid container={true}>
        {/* Employer or AR Details */}
        <BorderedGridItem xs={12} sm={4}>
          <ChecklistTarget name={'agree-to-esign'}
                           valid={store.didAgreeToSign}>
            <ReadonlyFieldLabel>
              <RequiredWrapper>{translations.signatureLabel}</RequiredWrapper>
            </ReadonlyFieldLabel>
            <ReadonlyFieldValue>
              <FieldCheckBox type={FieldType.CHECKBOX}
                             label={<RequiredWrapper>{translations.agreeToSignLabel}</RequiredWrapper>}
                             id={'signature'}
                             value={store.didAgreeToSign}
                             helperText={(
                               <ConditionalError
                                 condition={!store.didAgreeToSign && validationErrors.confirmAgreeToSign}>
                                 {validationErrorTranslations.confirmAgreeToSign}
                               </ConditionalError>
                             )}
                             setValueMethod={didAgree => store.setDidAgreeToSign(didAgree)}/>
            </ReadonlyFieldValue>
          </ChecklistTarget>
        </BorderedGridItem>
        <BorderedGridItem xs={12} sm={4}>
          <DatePickerChecklistTarget
              name={'todays-date'}
              valid={isTodaysLocalFormattedDate(store.signedOn)}
              hasError={hasSignedOnError}>
            <ReadonlyFieldLabel>
              <RequiredWrapper>{translations.todaysDateLabel}</RequiredWrapper>
            </ReadonlyFieldLabel>
            <ReadonlyFieldValue disableFlex={!hasSignedOnError}>
                <FormControl error={hasSignedOnError}>
                    <DatePickerAlignment>
                        <TodaysDatePicker
                            id={'todays-date'}
                            value={store.signedOn ? new Date(store.signedOn) : null}
                            inputVariant={'outlined'}
                            format={DateFormatTemplate.MonthFirstCalendarDate}
                            error={hasSignedOnError}
                            minDate={today}
                            maxDate={today}
                            onChange={(date: MaterialUiPickersDate) => {
                                store.setSignedOn(date?.format(DateFormatTemplate.MonthFirstCalendarDate) ?? '');
                            }}
                            fullWidth={true}
                        />
                    </DatePickerAlignment>
                    <HelperTextContainer>
                        <ConditionalError condition={
                            hasSignedOnError
                        }>
                            {validationErrorTranslations.provideSignDate}
                        </ConditionalError>
                    </HelperTextContainer>
                </FormControl>
            </ReadonlyFieldValue>
          </DatePickerChecklistTarget>
        </BorderedGridItem>
        { workerStatus !== RECEIPT_RPLACEMENT && <BorderedGridItem xs={12} sm={4}>
          <ReadonlyFieldLabel>{translations.titleLabel}</ReadonlyFieldLabel>
          <ReadonlyFieldValue disableFlex={true}>
            <SpacedPrefilledFormField>{store.title}</SpacedPrefilledFormField>
          </ReadonlyFieldValue>
        </BorderedGridItem>}
        {/* Employer or AR Details */}
        <BorderedGridItem xs={12} sm={4}>
          <ChecklistTarget name={'last-name'}
                           valid={!!store.lastName}>
            <ReadonlyFieldLabel>
              <RequiredWrapper>{translations.lastNameLabel}</RequiredWrapper>
            </ReadonlyFieldLabel>
            <ReadonlyFieldValue>
              <FieldText type={FieldType.TEXT}
                         id={'last-name'}
                         fullWidth={true}
                         value={store.lastName}
                         helperText={(
                           <ConditionalError condition={!store.lastName && validationErrors.provideLastName}>
                             {validationErrorTranslations.provideLastName}
                           </ConditionalError>
                         )}
                         setValueMethod={name => store.setLastName(name)}/>
            </ReadonlyFieldValue>
          </ChecklistTarget>
        </BorderedGridItem>
        <BorderedGridItem xs={12} sm={4}>
          <ChecklistTarget name={'first-name'}
                           valid={!!store.firstName}>
            <ReadonlyFieldLabel>
              <RequiredWrapper>{translations.firstNameLabel}</RequiredWrapper>
            </ReadonlyFieldLabel>
            <ReadonlyFieldValue>
              <FieldText type={FieldType.TEXT}
                         id={'first-name'}
                         fullWidth={true}
                         value={store.firstName}
                         helperText={(
                           <ConditionalError condition={!store.firstName && validationErrors.provideFirstName}>
                             {validationErrorTranslations.provideFirstName}
                           </ConditionalError>
                         )}
                         setValueMethod={name => store.setFirstName(name)}/>
            </ReadonlyFieldValue>
          </ChecklistTarget>
        </BorderedGridItem>
        {workerStatus !== RECEIPT_RPLACEMENT  && <React.Fragment>
          <BorderedGridItem xs={12} sm={4}>
            <ReadonlyFieldLabel>{translations.businessNameLabel}</ReadonlyFieldLabel>
            <ReadonlyFieldValue disableFlex={true}>
            <SpacedPrefilledFormField>{store.businessName}</SpacedPrefilledFormField>
            </ReadonlyFieldValue>
          </BorderedGridItem>
          {/* Employer Address */}
          <BorderedGridItem xs={12} sm={true}>
            <ReadonlyFieldLabel>{translations.businessStreetAddressLabel}</ReadonlyFieldLabel>
            <ReadonlyFieldValue>
            <SpacedPrefilledFormField>{store.businessAddress.streetAddress}</SpacedPrefilledFormField>
            </ReadonlyFieldValue>
          </BorderedGridItem>
          <BorderedGridItem xs={12} sm={3}>
            <ReadonlyFieldLabel>{translations.cityLabel}</ReadonlyFieldLabel>
            <ReadonlyFieldValue>
            <SpacedPrefilledFormField>{store.businessAddress.city}</SpacedPrefilledFormField>
            </ReadonlyFieldValue>
          </BorderedGridItem>
          <BorderedGridItem xs={12} sm={2}>
            <ReadonlyFieldLabel>{translations.stateLabel}</ReadonlyFieldLabel>
            <ReadonlyFieldValue>
            <SpacedPrefilledFormField>{store.businessAddress.state}</SpacedPrefilledFormField>
            </ReadonlyFieldValue>
          </BorderedGridItem>
          <BorderedGridItem xs={12} sm={2}>
            <ReadonlyFieldLabel>{translations.zipCodeLabel}</ReadonlyFieldLabel>
            <ReadonlyFieldValue>
            <SpacedPrefilledFormField>{store.businessAddress.zipCode}</SpacedPrefilledFormField>
            </ReadonlyFieldValue>
          </BorderedGridItem>
          </React.Fragment>}
      </BorderedGrid>
    );
  }
);

export default Certification;



// WEBPACK FOOTER //
// ./src/containers/form-i9/certification.tsx